import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { X } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { apiService, queryKeys } from '@/services/api';
import type { HeadToHeadResponse } from '@/types/api';
import HeadToHeadChart, { buildChartData, type ChartRow } from './HeadToHeadChart';

// Match colors from HeadToHeadChart
const PLAYER_A_COLOR = '#6366f1'; // indigo-500
const PLAYER_B_COLOR = '#475569'; // slate-600

interface HeadToHeadModalProps {
  isOpen: boolean;
  onClose: () => void;
  playerAId: string;
  playerAName: string;
  playerBId: string;
  playerBName: string;
  initialGameType?: '8-ball' | '9-ball' | '10-ball';
  divisionId?: string;
}

type GameTypeOption = '8-ball' | '9-ball' | '10-ball' | 'all';

const GAME_OPTIONS: { value: GameTypeOption; label: string }[] = [
  { value: '8-ball', label: '8-Ball' },
  { value: '9-ball', label: '9-Ball' },
  { value: '10-ball', label: '10-Ball' },
  { value: 'all', label: 'All' },
];

// ---------------------------------------------------------------------------
// Race History Sidebar — horizontal bars extending left/right
// ---------------------------------------------------------------------------
const MAX_RACE = 10;

function RaceSidebar({
  chartData,
  playerAName,
  playerBName,
  hoveredIndex,
  showGameType,
}: {
  chartData: ChartRow[];
  playerAName: string;
  playerBName: string;
  hoveredIndex: number | null;
  showGameType?: boolean;
}) {
  const rows = chartData.filter((r) => r.playerA_race != null || r.playerB_race != null);
  if (!rows.length) return null;

  const initials = (name: string) => {
    const parts = name.trim().split(/\s+/);
    return parts.length >= 2 ? `${parts[0][0]}${parts[parts.length - 1][0]}` : name.slice(0, 2);
  };

  const maxRace = Math.max(
    MAX_RACE,
    ...rows.map((r) => Math.max(r.playerA_race ?? 0, r.playerB_race ?? 0)),
  );

  const BALL_BADGE: Record<string, { bg: string; fg: string; label: string }> = {
    '8-ball':  { bg: '#1e1e1e', fg: '#fff', label: '8' },
    '9-ball':  { bg: '#fbbf24', fg: '#1e1e1e', label: '9' },
    '10-ball': { bg: '#3b82f6', fg: '#fff', label: '10' },
  };

  return (
    <div className="w-[140px] flex-shrink-0 border-l border-slate-100 overflow-y-auto text-[11px] hidden md:block">
      {/* Header */}
      <div className="sticky top-0 bg-white z-10 border-b border-slate-200 py-1.5 px-1.5">
        <div className="flex items-center justify-between px-0.5">
          <span className="font-bold uppercase tracking-wide" style={{ color: PLAYER_A_COLOR }}>{initials(playerAName)}</span>
          <span className="text-[10px] text-slate-400 font-medium">Race</span>
          <span className="font-bold uppercase tracking-wide" style={{ color: PLAYER_B_COLOR }}>{initials(playerBName)}</span>
        </div>
      </div>
      {/* Rows */}
      {rows.map((row) => {
        const aRace = row.playerA_race ?? 0;
        const bRace = row.playerB_race ?? 0;
        const isHovered = hoveredIndex === row.matchIndex;
        const aPct = maxRace > 0 ? (aRace / maxRace) * 100 : 0;
        const bPct = maxRace > 0 ? (bRace / maxRace) * 100 : 0;
        const badge = showGameType ? BALL_BADGE[row.gameType] : null;

        return (
          <div
            key={row.matchIndex}
            className={`flex items-center gap-0 py-1 px-1.5 border-b transition-colors ${
              isHovered ? 'bg-indigo-50 border-indigo-200' : 'border-slate-50'
            }`}
            title={`${row.displayDate}${showGameType ? ` (${row.gameType})` : ''}: ${aRace}–${bRace}`}
          >
            {/* Player A bar (right-aligned, grows leftward) */}
            <div className="flex-1 flex items-center justify-end gap-1">
              <span className={`text-[11px] font-bold tabular-nums ${isHovered ? 'text-indigo-700' : 'text-slate-600'}`} style={{ minWidth: 12, textAlign: 'right' }}>
                {aRace || '–'}
              </span>
              <div className="w-[32px] h-[8px] rounded-sm bg-slate-100 overflow-hidden flex justify-end">
                <div
                  className="h-full rounded-sm transition-all"
                  style={{
                    width: `${aPct}%`,
                    backgroundColor: isHovered ? PLAYER_A_COLOR : `${PLAYER_A_COLOR}99`,
                  }}
                />
              </div>
            </div>

            {/* Game type ball badge (replaces plain divider in "All" mode) */}
            {badge ? (
              <span
                className="mx-0.5 inline-flex items-center justify-center w-3.5 h-3.5 rounded-full text-[7px] font-bold flex-shrink-0"
                style={{ backgroundColor: badge.bg, color: badge.fg }}
              >
                {badge.label}
              </span>
            ) : (
              <div className={`mx-0.5 w-px h-3.5 ${isHovered ? 'bg-indigo-300' : 'bg-slate-200'}`} />
            )}

            {/* Player B bar (left-aligned, grows rightward) */}
            <div className="flex-1 flex items-center gap-1">
              <div className="w-[32px] h-[8px] rounded-sm bg-slate-100 overflow-hidden">
                <div
                  className="h-full rounded-sm transition-all"
                  style={{
                    width: `${bPct}%`,
                    backgroundColor: isHovered ? PLAYER_B_COLOR : `${PLAYER_B_COLOR}99`,
                  }}
                />
              </div>
              <span className={`text-[11px] font-bold tabular-nums ${isHovered ? 'text-slate-800' : 'text-slate-600'}`} style={{ minWidth: 12 }}>
                {bRace || '–'}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

const HeadToHeadModal: React.FC<HeadToHeadModalProps> = ({
  isOpen,
  onClose,
  playerAId,
  playerAName,
  playerBId,
  playerBName,
  initialGameType = '8-ball',
  // divisionId reserved for future per-division filtering
}) => {
  const [gameType, setGameType] = useState<GameTypeOption>(initialGameType);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  useEffect(() => {
    if (isOpen) setGameType(initialGameType);
  }, [isOpen, initialGameType]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  // Pass undefined to API when "all" is selected (no filter)
  const apiGameType = gameType === 'all' ? undefined : gameType;

  const { data, isLoading, error, refetch, isFetching } = useQuery<HeadToHeadResponse>({
    queryKey: queryKeys.headToHead(playerAId, playerBId, apiGameType),
    queryFn: () => apiService.getHeadToHead(playerAId, playerBId, apiGameType),
    staleTime: 5 * 60 * 1000,
    enabled: isOpen,
    placeholderData: (prev) => prev, // keep previous data while fetching new game type
  });

  const encounters = useMemo(() => data?.encounters ?? [], [data]);
  const isAllMode = gameType === 'all';
  const chartData = useMemo(() => buildChartData(encounters, isAllMode), [encounters, isAllMode]);
  const summary = data?.summary;

  const handleHoveredIndex = useCallback((index: number | null) => {
    setHoveredIndex(index);
  }, []);

  if (!isOpen) return null;

  const totalWins = (summary?.player_a_wins ?? 0) + (summary?.player_b_wins ?? 0);
  const aWinPct = totalWins > 0 ? ((summary?.player_a_wins ?? 0) / totalWins) * 100 : 0;
  const bWinPct = totalWins > 0 ? ((summary?.player_b_wins ?? 0) / totalWins) * 100 : 0;

  const streakText = (() => {
    if (!summary) return '';
    if (summary.player_a_current_streak > 0) return `${playerAName} on ${summary.player_a_current_streak}-match streak`;
    if (summary.player_b_current_streak > 0) return `${playerBName} on ${summary.player_b_current_streak}-match streak`;
    return 'No active streak';
  })();

  return (
    <div className="fixed inset-0 z-50 modal-backdrop flex items-end md:items-center justify-center p-0 md:p-4" onClick={onClose}>
      <div
        className="bg-white w-full h-full md:h-auto md:rounded-2xl shadow-2xl md:max-w-5xl md:max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-white px-4 md:px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h2 className="text-base md:text-lg font-bold text-slate-900 truncate">
            {playerAName} <span className="text-sm font-medium text-slate-400 mx-1">vs</span> {playerBName}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-100 active:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        {isLoading && !data ? (
          <div className="flex-1 flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-2 border-slate-200 border-t-indigo-600 rounded-full animate-spin" />
              <p className="text-sm text-slate-500">Loading head-to-head data...</p>
            </div>
          </div>
        ) : error ? (
          <div className="flex-1 flex items-center justify-center py-20">
            <div className="text-center">
              <p className="text-sm text-red-600 mb-3">Failed to load head-to-head data.</p>
              <button
                onClick={() => refetch()}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors"
              >
                Retry
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Summary Bar */}
            {summary && encounters.length > 0 && (
              <div className="px-4 md:px-6 py-3 bg-slate-50/50 border-b border-slate-100">
                {/* Win bar with midpoint indicator */}
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[11px] font-semibold shrink-0 tabular-nums" style={{ color: PLAYER_A_COLOR }}>
                    {summary.player_a_wins} {summary.player_a_wins === 1 ? 'Win' : 'Wins'}
                  </span>
                  <div className="relative flex-1 h-3 rounded-full bg-slate-100 overflow-visible">
                    {/* Bar fills */}
                    <div className="absolute inset-0 rounded-full overflow-hidden flex">
                      <div
                        className="h-full transition-all"
                        style={{
                          width: aWinPct > 0 ? `${Math.max(aWinPct, 4)}%` : '0%',
                          backgroundColor: PLAYER_A_COLOR,
                          borderRadius: bWinPct > 0 ? '9999px 0 0 9999px' : '9999px',
                        }}
                      />
                      <div
                        className="h-full transition-all ml-auto"
                        style={{
                          width: bWinPct > 0 ? `${Math.max(bWinPct, 4)}%` : '0%',
                          backgroundColor: PLAYER_B_COLOR,
                          borderRadius: aWinPct > 0 ? '0 9999px 9999px 0' : '9999px',
                        }}
                      />
                    </div>
                    {/* 50% midpoint marker */}
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center pointer-events-none" style={{ zIndex: 2 }}>
                      <div className="w-px h-5 border-l border-dashed border-slate-400" />
                    </div>
                    <div className="absolute left-1/2 -translate-x-1/2 pointer-events-none" style={{ top: '-14px', zIndex: 3 }}>
                      <span className="text-[10px]">⚔️</span>
                    </div>
                  </div>
                  <span className="text-[11px] font-semibold shrink-0 tabular-nums" style={{ color: PLAYER_B_COLOR }}>
                    {summary.player_b_wins} {summary.player_b_wins === 1 ? 'Win' : 'Wins'}
                  </span>
                </div>

                {/* Stats row */}
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
                  <span>{streakText}</span>
                  <span className="text-slate-300">|</span>
                  <span>
                    Avg CSR: {summary.player_a_avg_csr?.toFixed(1) ?? '—'} vs{' '}
                    {summary.player_b_avg_csr?.toFixed(1) ?? '—'}
                  </span>
                  <span className="text-slate-300">|</span>
                  <span>{summary.total_matches} matches</span>
                </div>
              </div>
            )}

            {/* Toolbar — always visible so user can switch back */}
            <div className="flex items-center gap-3 md:gap-4 px-4 md:px-6 py-3 border-b border-slate-100">
              <div className="flex gap-1 p-0.5 bg-slate-200/60 rounded-lg">
                {GAME_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setGameType(opt.value)}
                    className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                      gameType === opt.value
                        ? 'bg-white shadow-sm text-slate-800'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {encounters.length === 0 && !isFetching ? (
              <div className="flex-1 flex items-center justify-center py-20">
                <p className="text-sm text-slate-500">
                  No matches found between {playerAName} and {playerBName}{gameType !== 'all' ? ` for ${gameType}` : ''}.
                </p>
              </div>
            ) : (
              <>
                {/* Chart Area with Race Sidebar */}
                <div className="flex-1 flex" style={{ minHeight: 300, height: 400 }}>
                  <div className="flex-1 p-3 md:p-6 md:pr-0">
                    <HeadToHeadChart
                      encounters={encounters}
                      playerAId={playerAId}
                      playerAName={playerAName}
                      playerBId={playerBId}
                      playerBName={playerBName}
                      showGameType={gameType === 'all'}
                      onHoveredIndex={handleHoveredIndex}
                    />
                  </div>
                  <RaceSidebar
                    chartData={chartData}
                    playerAName={playerAName}
                    playerBName={playerBName}
                    hoveredIndex={hoveredIndex}
                    showGameType={isAllMode}
                  />
                </div>

                {/* Legend */}
                <div className="px-4 md:px-6 py-3 border-t border-slate-100 flex flex-wrap justify-center gap-x-6 gap-y-1">
                  <span className="flex items-center gap-1.5 text-xs text-slate-600">
                    <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: PLAYER_A_COLOR }} />
                    {playerAName}
                  </span>
                  <span className="flex items-center gap-1.5 text-xs text-slate-600">
                    <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: PLAYER_B_COLOR }} />
                    {playerBName}
                  </span>
                  <span className="flex items-center gap-1.5 text-xs text-slate-600">
                    <span className="inline-block w-2.5 h-2.5 rounded-full bg-emerald-500" />
                    Win
                  </span>
                  <span className="flex items-center gap-1.5 text-xs text-slate-600">
                    <span className="inline-block w-2.5 h-2.5 rounded-full bg-red-500" />
                    Loss
                  </span>
                  {gameType === 'all' && (
                    <>
                      <span className="border-l border-slate-200 pl-4 flex items-center gap-1.5 text-xs text-slate-600">
                        <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#1e1e1e] text-[7px] font-bold text-white">8</span>
                        8-Ball
                        <svg width="20" height="8"><line x1="0" y1="4" x2="20" y2="4" stroke="#94a3b8" strokeWidth="2" /></svg>
                      </span>
                      <span className="flex items-center gap-1.5 text-xs text-slate-600">
                        <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-amber-400 text-[7px] font-bold text-[#1e1e1e]">9</span>
                        9-Ball
                        <svg width="20" height="8"><line x1="0" y1="4" x2="20" y2="4" stroke="#94a3b8" strokeWidth="2" strokeDasharray="6 3" /></svg>
                      </span>
                      <span className="flex items-center gap-1.5 text-xs text-slate-600">
                        <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-blue-500 text-[7px] font-bold text-white">10</span>
                        10-Ball
                        <svg width="20" height="8"><line x1="0" y1="4" x2="20" y2="4" stroke="#94a3b8" strokeWidth="2" strokeDasharray="2 3" /></svg>
                      </span>
                    </>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default HeadToHeadModal;
